import { Injectable, OnDestroy } from '@angular/core';
import * as L from 'leaflet';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { UtilsService } from '../../services/utils.service';
import { createNavigationGlobalIcon } from '../../shared/service/icon.service';
import {
  AIS_VESSEL_CATEGORIES,
  categoryColor,
  categoryLabel,
} from './playback/aisstream-vessel-types';
import {
  PlaybackController,
  PlaybackState,
  EMPTY_PLAYBACK_STATE,
} from './playback/playback-controller';

export type AisstreamMode = 'live' | 'playback' | 'idle';

/** One entry per ship-type category present in the loaded playback data. */
export interface AisAvailableType {
  code: string;
  label: string;
  color: string;
  count: number;
}

/**
 * Component-scoped service (declared in providers[] of LeafletNewcollectionsComponent).
 * Angular creates and destroys one instance per component instance — no manual cleanup needed.
 *
 * Owns ALL AISstream-specific logic:
 *   - SSE EventSource lifecycle (live mode)
 *   - History HTTP fetch + step storage (playback mode)
 *   - Vessel marker rendering on the Leaflet map
 *
 * Component integration (3 lines only):
 *   1. @Component({ providers: [AisstreamLayerService] })
 *   2. constructor(public aisstreamLayer: AisstreamLayerService) {}
 *   3. this.aisstreamLayer.attach(this.map, taskId, collection) when an AISstream collection opens
 */
@Injectable()
export class AisstreamLayerService extends PlaybackController implements OnDestroy {
  private map:         L.Map | null = null;
  private vesselLayer: L.LayerGroup | null = null;
  private liveSource:  EventSource | null = null;
  private destroy$   = new Subject<void>();
  private aoiLayer:   L.GeoJSON | null = null;
  private aoiGeoJson: any = null;

  mode: AisstreamMode = 'idle';

  // ── PlaybackController state stream ──────────────────────────────────────────
  private _state$ = new BehaviorSubject<PlaybackState>({ ...EMPTY_PLAYBACK_STATE });
  readonly state$: Observable<PlaybackState> = this._state$.asObservable();
  /** ISO timestamp of the most recent data (live frame or loaded history). */
  private _lastUpdated: string | null = null;
  /** True while a history load is in flight. */
  private _loading = false;

  /** Steps fetched for the slider. Component reads this to populate sliderDateRangeArray. */
  steps: Array<{ time: string; vessels: any[] }> = [];

  // ── Playback engine state ───────────────────────────────────────────────────
  /** Playback timer (single ~200ms tick drives the animation). */
  private playTimer: any = null;
  private static readonly TICK_MS = 200;

  /** Per-step lookups precomputed on load for fast interpolation. */
  private _stepTimes: number[] = [];                 // epoch ms per step
  private _stepMaps: Array<Map<string, any>> = [];   // mmsi -> vessel per step
  private _virtualTime = 0;                          // current epoch ms on the timeline
  private _liveTaskId: string | null = null;         // remembered so X can resume live
  private _lastLiveVessels: any[] = [];              // last SSE frame — for live filtering

  isPlaying = false;
  speed = 60;                                        // simulated-seconds per real-second
  currentIndex = 0;
  showLabels = false;                                // vessel name labels off until toggled (T icon)
  /** Empty set = show all categories. */
  typeFilter = new Set<string>();

  constructor(
    private apiService:   ApiService,
    private utilsService: UtilsService,
  ) {
    super();
    this._emit();
  }

  // ── PlaybackController glue ──────────────────────────────────────────────────

  snapshot(): PlaybackState {
    return this._state$.value;
  }

  /** Rebuild + publish the current state snapshot for the toolbar controls. */
  private _emit(): void {
    this._state$.next({
      mode: this.mode,
      isPlaying: this.isPlaying,
      currentIndex: this.currentIndex,
      stepCount: this.steps.length,
      stepLabels: this.stepLabels(),
      speed: this.speed,
      showLabels: this.showLabels,
      typeFilter: new Set(this.typeFilter),
      availableTypes: this.availableTypes(),
      lastUpdated: this._lastUpdated,
      loading: this._loading,
    });
  }

  /** Load history for a UTC range and enter playback (PlaybackController). */
  enterPlayback(fromIso: string, toIso: string): Promise<number> {
    const id = this._liveTaskId;
    if (!id) return Promise.resolve(0);
    return this.loadHistory(id, fromIso, toIso);
  }

  /**
   * Optional hook set by the host component so the (collection-specific) history
   * date picker stays where the runtime TOI window lives. The `history-entry` and
   * `date-range` toolbar controls invoke this via `requestHistory()`.
   */
  onRequestHistory: (() => void) | null = null;

  override requestHistory(): void {
    this.onRequestHistory?.();
  }

  /** Tear down the toolbar entirely (PlaybackController). */
  close(): void {
    this.detach();
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  /** Call when an AISstream collection is selected. */
  attach(map: L.Map, taskId: string, collection: any): void {
    this.detach(); // clean up any previous state
    this.map         = map;
    this.vesselLayer = L.layerGroup().addTo(map);
    this._drawAoi(collection);
    this._startLive(taskId);
  }

  /** Call when switching to a different collection or entering playback mode. */
  detach(): void {
    this._stopLive();
    this.pause();
    this.vesselLayer?.clearLayers();
    this._markers.clear();
    if (this.vesselLayer && this.map) {
      this.map.removeLayer(this.vesselLayer);
    }
    if (this.aoiLayer && this.map) {
      try { this.map.removeLayer(this.aoiLayer); } catch (_) {}
    }
    this.vesselLayer = null;
    this.aoiLayer    = null;
    this.aoiGeoJson  = null;
    this.map         = null;
    this.steps       = [];
    this._stepTimes  = [];
    this._stepMaps   = [];
    this._virtualTime = 0;
    this.currentIndex = 0;
    this._lastLiveVessels = [];
    this.typeFilter  = new Set<string>();
    this.mode        = 'idle';
    this._lastUpdated = null;
    this._loading = false;
    this._emit();
  }

  /**
   * Re-attaches the target AOI polygon after resetMapNew() removes all non-tile layers.
   * No-op when no AOI is set.
   */
  ensureAoiLayerOnMap(): void {
    if (!this.aoiGeoJson || !this.map) return;
    try {
      if (this.aoiLayer) {
        if (!this.map.hasLayer(this.aoiLayer)) {
          this.aoiLayer.addTo(this.map);
        }
      } else {
        this.aoiLayer = this._createAoiGeoJson(this.aoiGeoJson).addTo(this.map);
      }
    } catch (_) {}
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.detach();
  }

  // ── Live mode ──────────────────────────────────────────────────────────────

  private _startLive(taskId: string): void {
    this._stopLive();
    this.mode        = 'live';
    this._liveTaskId = taskId;
    this.liveSource  = this.apiService.openAisstreamLive(taskId);
    this.liveSource.addEventListener('vessels', (e: MessageEvent) => {
      try {
        const vessels = JSON.parse(e.data);
        this._lastLiveVessels = vessels;
        this._renderVessels(vessels);
        this._lastUpdated = new Date().toISOString();
        this._emit();
      } catch { /* malformed frame — skip */ }
    });
    // EventSource reconnects automatically on onerror — no extra handling needed
    this._emit();
  }

  private _stopLive(): void {
    this.liveSource?.close();
    this.liveSource = null;
  }

  // ── Playback mode ──────────────────────────────────────────────────────────

  /** Fetch all steps for the given UTC range. Stops live stream while fetching. */
  loadHistory(taskId: string, fromIso: string, toIso: string): Promise<number> {
    this._stopLive();
    this.pause();
    this.mode = 'playback';
    this._liveTaskId = taskId;
    this._loading = true;
    this._emit();
    return new Promise((resolve, reject) => {
      this.apiService
        .getAisstreamHistory(taskId, fromIso, toIso)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.steps = res.steps ?? [];
            this._buildTimeline();
            this.currentIndex = 0;
            this._virtualTime = this._stepTimes[0] ?? 0;
            this._renderAtTime(this._virtualTime);
            this._lastUpdated = this.steps.length
              ? this.steps[this.steps.length - 1].time
              : this._lastUpdated;
            this._loading = false;
            this._emit();
            resolve(this.steps.length);
          },
          error: (err) => {
            // The toolbar swaps live→playback off `mode`, which was set
            // optimistically above. Without reverting it here a failed history
            // load strands the user in an empty playback bar with the live bar
            // gone, recoverable only by pressing X.
            this._loading = false;
            this.mode = 'live';
            this.steps = [];
            this._buildTimeline();
            this._emit();
            this.resumeLive(taskId);
            reject(err);
          },
        });
    });
  }

  /** Precompute per-step epoch times and mmsi→vessel lookups for interpolation. */
  private _buildTimeline(): void {
    this._stepTimes = this.steps.map((s) => new Date(s.time).getTime());
    this._stepMaps  = this.steps.map(
      (s) => new Map<string, any>(
        (s.vessels ?? []).map((v) => [String(v.staticData?.mmsi ?? v.id), v]),
      ),
    );
  }

  /** ISO/UTC label per step — drives the toolbar timestamp + slider. */
  stepLabels(): string[] {
    return this.steps.map((s) => s.time);
  }

  /** Distinct ship-type categories present in the current data (for the filter chips). */
  availableTypes(): AisAvailableType[] {
    const counts = new Map<string, number>();
    const collect = (vessels: any[]) => {
      for (const v of vessels ?? []) {
        const code = String(v.shipTypeCategory ?? 'OTHER');
        counts.set(code, (counts.get(code) ?? 0) + 1);
      }
    };
    if (this.mode === 'live') {
      collect(this._lastLiveVessels);
    } else {
      for (const step of this.steps) collect(step.vessels);
    }
    return AIS_VESSEL_CATEGORIES
      .filter((c) => counts.has(c.code))
      .map((c) => ({ ...c, count: counts.get(c.code)! }));
  }

  // ── Playback transport ───────────────────────────────────────────────────────

  play(): void {
    if (this.isPlaying || this.steps.length < 2) return;
    // Restart from the beginning if parked at the end.
    if (this._virtualTime >= this._lastStepTime()) {
      this._virtualTime = this._stepTimes[0] ?? 0;
    }
    this.isPlaying = true;
    this.playTimer = setInterval(() => this._tick(), AisstreamLayerService.TICK_MS);
    this._emit();
  }

  pause(): void {
    this.isPlaying = false;
    if (this.playTimer) {
      clearInterval(this.playTimer);
      this.playTimer = null;
    }
    this._emit();
  }

  togglePlay(): void {
    this.isPlaying ? this.pause() : this.play();
  }

  /** Jump to a discrete step (slider drag / step buttons). */
  seek(index: number): void {
    const i = Math.max(0, Math.min(index, this.steps.length - 1));
    this.currentIndex = i;
    this._virtualTime = this._stepTimes[i] ?? 0;
    this._renderAtTime(this._virtualTime);
    this._emit();
  }

  stepForward(): void { this.seek(this.currentIndex + 1); }
  stepBack():    void { this.seek(this.currentIndex - 1); }
  skipToStart(): void { this.seek(0); }
  skipToEnd():   void { this.seek(this.steps.length - 1); }

  setSpeed(n: number): void { this.speed = n; this._emit(); }

  toggleLabels(): void {
    this.showLabels = !this.showLabels;
    this._renderAtTime(this._virtualTime);
    this._emit();
  }

  setTypeFilter(set: Set<string>): void {
    this.typeFilter = new Set(set);
    if (this.mode === 'live') {
      this._renderVessels(this._lastLiveVessels);
    } else {
      this._renderAtTime(this._virtualTime);
    }
    this._emit();
  }

  /** Backwards-compatible single-step render (kept for any external callers). */
  renderStep(index: number): void {
    this.seek(index);
  }

  /** Resume live stream after returning from playback (X on the toolbar). */
  resumeLive(taskId?: string): void {
    this.pause();
    const id = taskId ?? this._liveTaskId;
    if (id && this.mode !== 'live') this._startLive(id);
  }

  private _lastStepTime(): number {
    return this._stepTimes[this._stepTimes.length - 1] ?? 0;
  }

  private _tick(): void {
    // Advance virtual time by (real tick) × speed.
    this._virtualTime += AisstreamLayerService.TICK_MS * this.speed;
    const end = this._lastStepTime();
    if (this._virtualTime >= end) {
      this._virtualTime = end;
      this.currentIndex = this.steps.length - 1;
      this._renderAtTime(this._virtualTime);
      this.pause();
      return;
    }
    const prevIndex = this.currentIndex;
    this.currentIndex = this._indexAtTime(this._virtualTime);
    this._renderAtTime(this._virtualTime);
    // Render every tick (that is what makes vessels glide), but publish state
    // only when the visible step changes: _emit() rebuilds stepLabels and
    // rescans every step for availableTypes, which at 5 ticks/s over a long
    // history is a full O(steps × vessels) sweep several times a second.
    // The sibling controllers guard the same way.
    if (this.currentIndex !== prevIndex) this._emit();
  }

  /** Index of the last step whose time is <= virtual time. */
  private _indexAtTime(vt: number): number {
    let i = 0;
    while (i + 1 < this._stepTimes.length && this._stepTimes[i + 1] <= vt) i++;
    return i;
  }

  /**
   * Render vessels at an arbitrary point on the timeline, linearly interpolating each
   * vessel's position between the surrounding step fixes so markers glide smoothly.
   */
  private _renderAtTime(vt: number): void {
    if (!this.steps.length) return;
    const i = this._indexAtTime(vt);
    const baseMap = this._stepMaps[i];
    if (!baseMap) return;

    const nextMap = this._stepMaps[i + 1];
    const t0 = this._stepTimes[i];
    const t1 = this._stepTimes[i + 1];
    const frac = nextMap && t1 > t0 ? Math.max(0, Math.min(1, (vt - t0) / (t1 - t0))) : 0;

    const vessels: any[] = [];
    baseMap.forEach((v, mmsi) => {
      const p0 = v.lastPositionUpdate ?? {};
      if (p0.latitude == null || p0.longitude == null) return;

      let lat = p0.latitude;
      let lng = p0.longitude;
      const next = nextMap?.get(mmsi);
      const p1 = next?.lastPositionUpdate;
      if (frac > 0 && p1?.latitude != null && p1?.longitude != null) {
        lat = p0.latitude + (p1.latitude - p0.latitude) * frac;
        lng = p0.longitude + (p1.longitude - p0.longitude) * frac;
      }
      vessels.push({ ...v, lastPositionUpdate: { ...p0, latitude: lat, longitude: lng } });
    });

    this._renderVessels(vessels);
  }

  // ── AOI drawing ────────────────────────────────────────────────────────────

  private _drawAoi(collection: any): void {
    if (!this.map) return;
    try {
      const rawTarget = collection?.target;
      if (!rawTarget) return;
      this.aoiGeoJson = typeof rawTarget === 'string' ? JSON.parse(rawTarget) : rawTarget;
      this.aoiLayer   = this._createAoiGeoJson(this.aoiGeoJson).addTo(this.map);
      const bounds = this.aoiLayer.getBounds();
      if (bounds?.isValid()) this.map.fitBounds(bounds, { padding: [50, 50] });
    } catch (_) {}
  }

  private _createAoiGeoJson(geoJson: any): L.GeoJSON {
    return L.geoJSON(geoJson, {
      style: () => ({
        color: 'yellow',
        fillColor: 'transparent',
        weight: 2,
        dashArray: '6 4',
      }),
      interactive: false,
    });
  }

  // ── Rendering ──────────────────────────────────────────────────────────────

  /** Cache of rendered marker icons keyed by category color (the icon depends only on color). */
  private _iconCache = new Map<string, L.Icon>();

  /** Live marker pool keyed by MMSI — reused across renders to avoid DOM churn. */
  private _markers = new Map<string, L.Marker>();

  /** Get (or lazily build + cache) the marker icon for a category color. */
  private _iconForColor(color: string): L.Icon {
    let icon = this._iconCache.get(color);
    if (!icon) {
      const iconUrl = createNavigationGlobalIcon(color).toDataURL();
      icon = L.icon({ iconUrl, iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -14] });
      this._iconCache.set(color, icon);
    }
    return icon;
  }

  private _renderVessels(vessels: any[]): void {
    if (!this.vesselLayer || !this.map) return;

    // Re-add the layer group if resetMapNew() removed it from the map
    if (!this.map.hasLayer(this.vesselLayer)) {
      this.vesselLayer.addTo(this.map);
    }

    // Reuse markers across renders (keyed by MMSI) instead of recreating them every
    // tick — updating position/icon is far cheaper than tearing down + rebuilding DOM.
    const seen = new Set<string>();

    for (const vessel of vessels) {
      // Shape from backend toVesselGeoJson():
      // { id, staticData: { mmsi, name, shipType }, shipTypeCategory,
      //   lastPositionUpdate: { latitude, longitude, course, speed, navStatusLabel } }
      const pos  = vessel.lastPositionUpdate ?? {};
      const stat = vessel.staticData ?? {};
      const lat  = pos.latitude;
      const lng  = pos.longitude;
      if (lat == null || lng == null) continue;

      // Client-side vessel-type filter (empty set = show all)
      const category = String(vessel.shipTypeCategory ?? 'OTHER');
      if (this.typeFilter.size && !this.typeFilter.has(category)) continue;

      const key   = String(stat.mmsi ?? vessel.id ?? `${lat},${lng}`);
      const color = categoryColor(category);
      seen.add(key);

      let marker = this._markers.get(key);
      if (!marker) {
        marker = L.marker([lat, lng], {
          icon: this._iconForColor(color),
          zIndexOffset: 100000,
          riseOnHover:  true,
        });
        // Build the popup HTML lazily from the marker's latest stashed data (only on open)
        marker.bindPopup(
          (m: any) => this._buildVesselPopup(m._aisVessel, m._aisCategory, m._aisColor),
          { className: 'ais-vessel-popup', closeButton: false, offset: L.point(0, -6) },
        );
        this._markers.set(key, marker);
        this.vesselLayer.addLayer(marker);
      } else {
        marker.setLatLng([lat, lng]);
        if ((marker as any)._aisColor !== color) {
          marker.setIcon(this._iconForColor(color));
        }
      }

      // Stash latest data for the lazy popup + label
      (marker as any)._aisVessel   = vessel;
      (marker as any)._aisCategory = category;
      (marker as any)._aisColor    = color;

      // T-toggle: permanent name label when labels are on
      if (this.showLabels) {
        const name = String(stat.name ?? 'Unknown');
        if (marker.getTooltip()) {
          marker.setTooltipContent(name);
        } else {
          marker.bindTooltip(name, {
            permanent: true,
            direction: 'top',
            offset:    L.point(0, -14),
            className: 'ais-vessel-label',
          });
        }
      } else if (marker.getTooltip()) {
        marker.unbindTooltip();
      }
    }

    // Drop markers that are gone from this frame (or filtered out)
    this._markers.forEach((marker, key) => {
      if (!seen.has(key)) {
        this.vesselLayer!.removeLayer(marker);
        this._markers.delete(key);
      }
    });
  }

  /** Dark-themed vessel popup (design image 5): name, MMSI, Type, Status, SOG, COG, Time UTC. */
  private _buildVesselPopup(vessel: any, category: string, color: string): string {
    const pos  = vessel.lastPositionUpdate ?? {};
    const stat = vessel.staticData ?? {};
    const esc  = (s: any) => String(s ?? '').replace(/[<>&"]/g, (c) =>
      ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c] as string));

    const name   = esc(stat.name ?? 'Unknown');
    const mmsi   = esc(stat.mmsi ?? '—');
    const type   = esc(categoryLabel(category));
    const status = esc(pos.navStatusLabel ?? '—');
    const sog    = pos.speed != null ? `${esc(pos.speed)} kn` : '—';
    const cog    = pos.course != null ? `${esc(Math.round(Number(pos.course)))}°` : '—';
    const time   = pos.timestamp ? esc(this._formatUtc(pos.timestamp)) : '—';

    return (
      `<div class="ais-popup">` +
        `<div class="ais-popup__head"><span class="ais-popup__dot" style="background:${color}"></span>` +
          `<span class="ais-popup__name">${name}</span></div>` +
        `<div class="ais-popup__row"><span class="ais-popup__k">MMSI:</span><span class="ais-popup__mmsi">${mmsi}</span></div>` +
        `<div class="ais-popup__row"><span class="ais-popup__k">Type:</span><span class="ais-popup__v">${type}</span></div>` +
        `<div class="ais-popup__row"><span class="ais-popup__k">Status:</span><span class="ais-popup__v">${status}</span></div>` +
        `<div class="ais-popup__row"><span class="ais-popup__k">SOG:</span><span class="ais-popup__v">${sog}</span></div>` +
        `<div class="ais-popup__row"><span class="ais-popup__k">COG:</span><span class="ais-popup__v">${cog}</span></div>` +
        `<div class="ais-popup__row"><span class="ais-popup__k">Time:</span><span class="ais-popup__time">${time}</span></div>` +
      `</div>`
    );
  }

  /** Format an ISO timestamp as e.g. "Tue, 21 Jul 2026 19:47:18 UTC". */
  private _formatUtc(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    const days   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const p = (n: number) => String(n).padStart(2, '0');
    return `${days[d.getUTCDay()]}, ${p(d.getUTCDate())} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()} ` +
      `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())} UTC`;
  }
}
